import { useMemo } from 'react';
import { AccountType } from '@mews-commander/data-access';
import { EnterpriseLink, TruncatedText } from '@mews-commander/ui';
import { ColumnAlignment, MatrixMapping } from '@mews/b2b-ui';
import { DateTimeService, LocalizedDate, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { BulkAccountCheckbox, BulkInvoiceCheckbox } from '../components/CityLedgerTable/BulkActions';
import { getAgingCellColor, getLocalizedCurrencyValueCell, getStatusCell } from '../components/mapping';
import { AccountsTableData } from '../types';

const TEXT_LENGTH = 35;

type TableMapping = AccountsTableData & { actions?: React.ReactNode };

export const useColumns = () => {
    return useMemo((): Array<MatrixMapping<TableMapping>> => {
        return [
            {
                valueKey: 'invoiceNumber',
                columnAlignment: ColumnAlignment.Center,
                columnWidth: '12%',
                title: <LocalizedText textKey={TextKey.InvoiceNo} />,
                render: (data) => {
                    if (data?.invoiceNumber && data?.invoiceId) {
                        return (
                            <Stack spacing="150" itemAlignment="center">
                                <BulkInvoiceCheckbox data={data} />
                                <EnterpriseLink
                                    variant="basic"
                                    underline={false}
                                    to="Bill/Detail/$id"
                                    params={{ id: data.invoiceId }}
                                >
                                    {data.invoiceNumber}
                                </EnterpriseLink>
                            </Stack>
                        );
                    }

                    if (data?.ownerName && data?.ownerId) {
                        return (
                            <Stack spacing="150">
                                <BulkAccountCheckbox data={data} />
                                <EnterpriseLink
                                    variant="basic"
                                    underline={false}
                                    to={
                                        data.ownerType === AccountType.Company
                                            ? 'Company/Dashboard/$id'
                                            : 'Customer/Dashboard/$id'
                                    }
                                    params={{ id: data.ownerId }}
                                >
                                    <TruncatedText text={data.ownerName} maxLength={TEXT_LENGTH} />
                                </EnterpriseLink>
                            </Stack>
                        );
                    }

                    return data?.ownerName ? <TruncatedText text={data.ownerName} maxLength={TEXT_LENGTH} /> : null;
                },
            },
            {
                valueKey: 'associatedAccountName',
                title: <LocalizedText textKey={TextKey.CityLedgerAssociatedAccount} />,
                columnAlignment: ColumnAlignment.Center,
                columnWidth: '10%',
                render: (data) =>
                    data?.associatedAccountName && data?.associatedAccountId ? (
                        <EnterpriseLink
                            variant="basic"
                            underline={false}
                            to={
                                data.associatedAccountType === AccountType.Company
                                    ? 'Company/Dashboard/$id'
                                    : 'Customer/Dashboard/$id'
                            }
                            params={{ id: data.associatedAccountId }}
                        >
                            <TruncatedText text={data.associatedAccountName} maxLength={TEXT_LENGTH} />
                        </EnterpriseLink>
                    ) : null,
            },
            {
                valueKey: 'state',
                title: <LocalizedText textKey={TextKey.Status} />,
                columnAlignment: ColumnAlignment.Center,
                columnWidth: '8%',
                render: (data) => (data?.state ? getStatusCell(data.state) : null),
            },
            {
                valueKey: 'dueDateLocal',
                title: <LocalizedText textKey={TextKey.DueDate} />,
                columnWidth: '10%',
                columnAlignment: ColumnAlignment.Center,
                render: (data) => {
                    const { dueDateLocal } = data;

                    if (!dueDateLocal) {
                        return null;
                    }

                    const date = DateTimeService.fromLocalUtcString(dueDateLocal);

                    return <LocalizedDate date={date} />;
                },
            },
            {
                valueKey: 'ageingBalance',
                title: (
                    <>
                        <LocalizedText textKey={TextKey.DaysAging} /> <>[0-30]</>
                    </>
                ),
                columnAlignment: ColumnAlignment.Center,
                columnWidth: '8%',
                getCellColor: (data) =>
                    data.state ? getAgingCellColor(data?.state, !!data.ageingBalance?.lessThan30?.value) : undefined,
                render: (data) => {
                    const { ageingBalance } = data;
                    return getLocalizedCurrencyValueCell(ageingBalance?.lessThan30);
                },
            },
            {
                valueKey: 'ageingBalance',
                title: (
                    <>
                        <LocalizedText textKey={TextKey.DaysAging} /> <>[31-60]</>
                    </>
                ),
                columnWidth: '8%',
                columnAlignment: ColumnAlignment.Center,
                getCellColor: (data) =>
                    data.state ? getAgingCellColor(data?.state, !!data.ageingBalance?.upTo60?.value) : undefined,
                render: (data) => {
                    const { ageingBalance } = data;
                    return getLocalizedCurrencyValueCell(ageingBalance?.upTo60);
                },
            },
            {
                valueKey: 'ageingBalance',
                title: (
                    <>
                        <LocalizedText textKey={TextKey.DaysAging} /> <>[61-90]</>
                    </>
                ),
                columnWidth: '8%',
                columnAlignment: ColumnAlignment.Center,
                getCellColor: (data) =>
                    data.state ? getAgingCellColor(data?.state, !!data.ageingBalance?.upTo90?.value) : undefined,
                render: (data) => {
                    const { ageingBalance } = data;
                    return getLocalizedCurrencyValueCell(ageingBalance?.upTo90);
                },
            },
            {
                valueKey: 'ageingBalance',
                title: (
                    <>
                        <LocalizedText textKey={TextKey.DaysAging} /> <>[90+]</>
                    </>
                ),
                columnWidth: '8%',
                columnAlignment: ColumnAlignment.Center,
                getCellColor: (data) =>
                    data.state ? getAgingCellColor(data?.state, !!data.ageingBalance?.plus90?.value) : undefined,
                render: (data) => {
                    const { ageingBalance } = data;
                    return getLocalizedCurrencyValueCell(ageingBalance?.plus90);
                },
            },
            {
                valueKey: 'lastReminderDateLocal',
                title: <LocalizedText textKey={TextKey.Reminders} />,
                columnWidth: '10%',
                columnAlignment: ColumnAlignment.Center,
                render: (data) => {
                    const { lastReminderDateLocal } = data;
                    if (!lastReminderDateLocal) {
                        return null;
                    }

                    const date = DateTimeService.fromLocalUtcString(lastReminderDateLocal);

                    return <LocalizedDate date={date} />;
                },
            },
            {
                valueKey: 'amount',
                title: <LocalizedText textKey={TextKey.InvoiceAmount} />,
                columnWidth: '12%',
                columnAlignment: ColumnAlignment.Center,
                render: (data) => {
                    const { amount, totalAmount } = data;
                    return getLocalizedCurrencyValueCell(totalAmount ?? amount);
                },
            },
            {
                valueKey: 'balance',
                columnWidth: '12%',
                title: <LocalizedText textKey={TextKey.Balance} />,
                columnAlignment: ColumnAlignment.Center,
                render: (data) => {
                    const { balance, totalBalance } = data;
                    return getLocalizedCurrencyValueCell(totalBalance ?? balance);
                },
            },
        ];
    }, []);
};
